import * as ProgressPrimitive from '@radix-ui/react-progress';
import * as React from 'react';
import { cn } from '../lib/utils.js';

export interface ProgressRingProps
  extends Omit<React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root>, 'value'> {
  /** Filled share of the ring, 0 to 100. */
  value: number;
  /** Accessible name; the value is announced with it. */
  label: string;
}

/**
 * A small determinate ring: how much of a bounded thing is left. Radix owns
 * the progressbar role and value, as `Progress` does; the arc takes the
 * current text colour so a parent can tint it, and the track is muted.
 */
export const ProgressRing = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressRingProps
>(({ value, label, className, ...props }, ref) => {
  const clamped = Math.min(100, Math.max(0, value));
  const radius = 6;
  const circumference = 2 * Math.PI * radius;
  return (
    <ProgressPrimitive.Root
      ref={ref}
      value={clamped}
      aria-label={label}
      className={cn('inline-flex size-3.5 shrink-0', className)}
      {...props}
    >
      <svg aria-hidden="true" viewBox="0 0 16 16" className="size-full -rotate-90">
        <circle cx="8" cy="8" r={radius} fill="none" strokeWidth="2.5" className="stroke-muted" />
        <circle
          cx="8"
          cy="8"
          r={radius}
          fill="none"
          strokeWidth="2.5"
          strokeLinecap="round"
          stroke="currentColor"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - clamped / 100)}
          className="transition-[stroke-dashoffset]"
        />
      </svg>
    </ProgressPrimitive.Root>
  );
});
ProgressRing.displayName = 'ProgressRing';
